/**
 * SUNTRONIX ONE — Lifecycle Sankey (SVG).
 *
 * Renders a horizontal 8-column lifecycle Sankey from
 * `/api/insights/lifecycle-sankey`.  Every node + link is clickable —
 * click drills to the underlying asset list using the `drill_to` filter
 * returned by the backend.  Never fabricates counts.
 *
 * Rendering rules:
 *  • Node heights use sqrt scaling so a single dominant bucket doesn't
 *    dwarf the rest of the estate.
 *  • Zero-value links are omitted (no fake flow implied).
 *  • Single-node payloads render as a static row (no divide-by-zero).
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";

const TONE = {
  procured: "#EFF5FF", deployed: "#ECFAFF", in_use: "#E6F7F8",
  support: "#FFF8E7", recovery: "#F5F0FF", reuse_ready: "#ECFDF6",
  redeployed: "#ECFDF6", recycled: "#ECFDF6",
};
const STROKE = {
  procured: "#246BFD", deployed: "#18BCEB", in_use: "#2DB7C4",
  support: "#F5A524", recovery: "#805AD5", reuse_ready: "#20C997",
  redeployed: "#20C997", recycled: "#169C70",
};

export default function LifecycleSankey({ data }) {
  const layout = useMemo(() => data ? buildLayout(data) : null, [data]);
  if (!data) return <div className="h-72 bg-slate-50 rounded animate-pulse" />;
  if (data.insufficient_data) {
    return (
      <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-slate-500 text-[13px]" data-testid="sankey-empty">
        Insufficient asset data to render the lifecycle flow yet.
      </div>
    );
  }
  const { nodes, links } = layout;
  const w = 1200, h = 340;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="lifecycle-sankey">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1">
          Technology Lifecycle · {(data.total_assets || 0).toLocaleString()} assets
          <HelpCircle className="h-3 w-3 opacity-50" title={data.definition} />
        </div>
        <div className="text-[10px] text-slate-400" title={data.note}>
          Edge widths reflect current-state floors.
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" className="overflow-visible">
        {links.map((l, i) => (
          <Link key={i} to={l.drill_to}>
            <path d={l.path} fill={STROKE[l.source] || "#CBD5E1"}
                  fillOpacity={0.35} stroke="none"
                  data-testid={`sankey-link-${l.source}-${l.target}`}>
              <title>{`${l.source} → ${l.target}: ${l.value.toLocaleString()} assets`}</title>
            </path>
          </Link>
        ))}
        {nodes.map((n) => (
          <Link key={n.id} to={n.drill_to}>
            <g data-testid={`sankey-node-${n.id}`}>
              <rect x={n.x} y={n.y} width={n.w} height={n.h}
                    fill={TONE[n.id] || "#F1F5F9"}
                    stroke={STROKE[n.id] || "#CBD5E1"} strokeWidth={1.5} rx={4}>
                <title>{`${n.label}: ${n.value.toLocaleString()}`}</title>
              </rect>
              <text x={n.x + n.w / 2} y={n.y + n.h / 2 - 4}
                    textAnchor="middle" className="fill-slate-900"
                    style={{ fontSize: 12, fontWeight: 600 }}>
                {n.label}
              </text>
              <text x={n.x + n.w / 2} y={n.y + n.h / 2 + 12}
                    textAnchor="middle" className="fill-slate-500"
                    style={{ fontSize: 11 }}>
                {n.value.toLocaleString()}
              </text>
            </g>
          </Link>
        ))}
      </svg>
    </div>
  );
}

function buildLayout({ nodes, links }) {
  const w = 1200, h = 340, pad = 30, nodeW = 118;
  const cols = Math.max(1, nodes.length - 1);
  const colGap = (w - pad * 2 - nodeW) / cols;
  const maxVal = Math.max(1, ...nodes.map((n) => n.value));
  // sqrt scaling — dampens dominance of a single bucket while keeping
  // zero buckets visibly small (min 34px so labels fit).
  const scaledNodes = nodes.map((n, i) => {
    const norm = Math.sqrt(n.value / maxVal);
    const nodeH = 34 + norm * (h - 100);
    return { ...n, x: pad + i * colGap, y: h / 2 - nodeH / 2,
              w: nodeW, h: nodeH };
  });
  const byId = Object.fromEntries(scaledNodes.map((n) => [n.id, n]));
  const scaledLinks = links
    .filter((l) => l.value > 0)   // hide zero-flow edges
    .map((l) => {
      const src = byId[l.source], tgt = byId[l.target];
      if (!src || !tgt) return { ...l, path: "" };
      const y1 = src.y + src.h / 2, y2 = tgt.y + tgt.h / 2;
      const x1 = src.x + src.w, x2 = tgt.x;
      const midX = (x1 + x2) / 2;
      const thick = Math.max(4, Math.sqrt(l.value / maxVal) * 30);
      const path = `M ${x1} ${y1 - thick / 2}
                       C ${midX} ${y1 - thick / 2}, ${midX} ${y2 - thick / 2}, ${x2} ${y2 - thick / 2}
                       L ${x2} ${y2 + thick / 2}
                       C ${midX} ${y2 + thick / 2}, ${midX} ${y1 + thick / 2}, ${x1} ${y1 + thick / 2}
                       Z`;
      return { ...l, path };
    });
  return { nodes: scaledNodes, links: scaledLinks };
}
